import { Card, Table, TableBody, TableContainer } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import {
  PositionTxType,
  PositionFilters,
  PositionFilterKey,
  PositionFilterValue,
} from 'src/type/position';
import { isAfter } from 'src/utils/format-time';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import { useBoolean } from 'src/hooks/use-boolean';
import { TransactionType, TransactionStatus } from 'src/type/transaction';
import usePagination from 'src/hooks/use-pagination';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';
import { UserType } from 'src/type/user';
import { ProductType } from 'src/type/product';
// import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
// import { BranchType } from 'src/type/branch';
import StockTableToolbar from './stock-table-toolbar';
import StockTableFiltersResult from './stock-table-filters-result';
import StockTableRow from './stock-table-row';
// import StockCreateDialog from './stock-create-dialog';

const defaultFilters: PositionFilters = {
  name: null,
  type: 'all',
  startDate: null,
  endDate: null,
  status: TransactionStatus.PENDING,
};

const tableHead = [
  { id: 'type', label: 'ประเภท', align: 'center' },
  { id: 'customer', label: 'ลูกค้า', align: 'left' },
  { id: 'pricePerUnit', label: 'ราคา/บาท', align: 'left' },
  { id: 'baht', label: 'จำนวน(บาท)', align: 'left' },
  { id: 'grams', label: 'จำนวน(กรัม)', align: 'left' },
  { id: 'total', label: 'ราคารวม', align: 'left' },
  { id: 'createdAt', label: 'วันที่ทำรายการ', width: 180, align: 'left' },
];

type Props = {
  user?: UserType;
};

export default function StockTableView({ user }: Props) {
  const initialize = useBoolean(true);
  const [filters, setFilters] = useState(defaultFilters);
  const form = useBoolean(false);
  const reload = useBoolean(false);
  const [filteredProductIds, setFilteredProductIds] = useState<string[]>([]);

  // const [branches, setBranches] = useState<BranchType[]>([]);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<PositionTxType>();

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  const canReset =
    !!filters.name || filters.type !== 'all' || (!!filters.startDate && !!filters.endDate);

  // Fetch products and filter out "รูปพรรณ"
  useEffect(() => {
    const handleGetProducts = async () => {
      try {
        const { data } = await axios.get<Pagination<ProductType>>(endpoints.product.get, {
          params: { limit: 999 },
        });
        const filtered = data.results
          .filter((product) => !product.name.includes('รูปพรรณ'))
          .map((product) => product.id);
        setFilteredProductIds(filtered);
      } catch (error) {
        toastError(error);
        setFilteredProductIds([]);
      }
    };
    handleGetProducts();
  }, []);

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      ...fDateRange(filters.startDate, filters.endDate),

      type:
        filters.type === 'all'
          ? `${TransactionType.BUY_POSITION},${TransactionType.SELL_POSITION},${TransactionType.DEPOSIT},${TransactionType.REDEEM}`
          : filters.type,
      status: `${TransactionStatus.SUCCESS}`,
      page: currentPage,
      limit: rowsPerPage,
      userId: user?.uid || filters.name?.value,
      ...(filteredProductIds.length > 0 && { productId: filteredProductIds.join(',') }),
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
      orderBy: 'createdAt:desc',
    };
    handleGetTx(params);
    // handleGetBranches();
    // handleGetMattProfit();
  }, [
    currentPage,
    rowsPerPage,
    filters.startDate,
    filters.endDate,
    filters.type,
    reload.value,
    filters.name,
    filteredProductIds,
  ]);

  // const handleGetMattProfit = useCallback(async () => {
  //   try {
  //     const { data } = await axios.get<number>(endpoints.stocks.getMattProfit);
  //     setMattProfit(data);
  //   } catch (error) {
  //     toastError(error);
  //   }
  // }, []);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const { data } = await axios.get<Pagination<PositionTxType>>(
        endpoints.transactions.stockReport,
        {
          params: fParams(params),
        }
      );

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleFilters = useCallback((name: PositionFilterKey, value: PositionFilterValue) => {
    onResetPage();
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  return (
    <>
      <Card>
        <StockTableToolbar
          filters={filters}
          onFilters={handleFilters}
          dateError={dateError}
          disabledSearch={!!user}
          enableCreate={!!user}
          onCreatePosition={form.onTrue}
          // mattProfit={mattProfit}
        />

        {canReset && (
          <StockTableFiltersResult
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            results={meta.totalResults}
            sx={{ p: 2.5, pt: 0 }}
          />
        )}

        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table sx={{ minWidth: 800 }}>
              <TableHeadCustom headLabel={tableHead} />

              <TableBody>
                <TableLoading loading={initialize.value} rows={rowsPerPage}>
                  {tableData.map((row) => (
                    <StockTableRow key={row.id} row={row} />
                  ))}

                  <TableEmptyRows
                    show={!initialize.value}
                    height={76}
                    emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                  />

                  <TableNoData notFound={!tableData.length} />
                </TableLoading>
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={meta.totalResults}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Card>
      {/* {!!form.value && !!user && (
        <StockCreateDialog
          open={form.value}
          onClose={form.onFalse}
          user={user}
          handleReload={handleReload}
        />
      )} */}
    </>
  );
}
